import "styles/modal.scss";
import "./DocumentsDrawer.scss";

import cx from "classnames";
import Button from "components/Button/Button";
import KaboodleToaster from "components/common/KaboodleToaster";
import LoadingIcon from "components/Loading/LoadingIcon";
import SearchBar from "components/SearchBar/SearchBar";
import type { ServerSortColumn } from "components/Table/Table";
import { FEATURE_FLAGS } from "constants/FeatureFlags";
import { convertSortToOrderBy } from "pages/finance/utils/reportUtils";
import { useCreateMetadoc } from "pages/metadoc/hooks/page-operations/useCreateMetadoc";
import { getMetadocNavigationContext } from "pages/metadoc/utils/metadocRoutes";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { setShowDocumentsDrawer } from "reducers/drawer";
import {
  useBulkDeleteDocumentsMutation,
  useBulkDownloadDocumentsMutation,
} from "services/documents-api";
import { useGetFileByIdQuery } from "services/file-api";
import {
  useAddDocumentsToMetadocMutation,
  useGetMetadocQuery,
} from "services/metadoc-api";
import { useGetOrganizationSettingsQuery } from "services/misc-api";
import type { RootState } from "services/store";
import { useDispatch, useSelector } from "utils/redux";
import { useUrlStore } from "utils/useUrlStore";

import type { DeleteModalState, DocumentType } from "../DocumentTypes";
import { useDocumentDropzone } from "../hooks/useDocumentDropzone";
import { useDocumentTable } from "../hooks/useDocumentTable";
import { useWorkspaceAutoFilters } from "../hooks/useWorkspaceAutoFilters";
import DeleteConfirmationModal from "../shared/DeleteConfirmationModal";
import DocumentTable from "../table/DocumentTable";
import UploadMetadataGate from "./UploadMetadataGate";
import type { UploadMetadataResult } from "./UploadMetadataGate";
import ExtractPayHistoryModal from "pages/future-medical/shared/medical-history/pay-history/ExtractPayHistoryModal";

const getApiErrorMessage = (error: unknown, fallback: string) =>
  error && typeof error === "object" && "data" in error
    ? (error.data as { error?: string })?.error || fallback
    : fallback;

type DocumentsProps = {
  fileId: number;
  serviceId?: number;
  serviceType?: string;
  additionalParams?: Record<string, string>;
  // Optional metadoc creation mode (for MetaDocPage)
  metaDoc?: boolean;
  selectedDocumentIds?: Set<number>;
  onSelectionChange?: (ids: Set<number>) => void;
};

/**
 * DocumentsDrawer - Main container for document management
 *
 * - shows source documents only
 * - drag & drop file upload with validation
 * - bulk download and delete operations
 * - Document search and filtering
 *
 * the drawer opens from the right side and provides full document CRUD operations.
 */
const Documents = ({
  fileId,
  serviceId,
  serviceType,
  additionalParams,
  metaDoc = false,
  selectedDocumentIds = new Set(),
  onSelectionChange,
}: DocumentsProps) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const {
    metadocId: routeMetadocId,
    fileId: routeFileId,
    documentManagementFileId,
    msaId,
    mcpId,
    lcpId,
    reviewId,
  } = useParams<{
    metadocId?: string;
    fileId?: string;
    documentManagementFileId?: string;
    msaId?: string;
    mcpId?: string;
    lcpId?: string;
    reviewId?: string;
  }>();
  const parsedMetadocId = routeMetadocId ? parseInt(routeMetadocId, 10) : null;
  const [selectedFile, setSelectedFile] = useState<File[] | null>(null);
  const [pendingFiles, setPendingFiles] = useState<File[] | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const showDocumentsDrawer = useSelector(
    (state: RootState) => state.drawer.showDocumentsDrawer,
  );
  const constants = useSelector((state: RootState) => state.shared.constants);

  // workspace-based auto-filtering
  const { setFilter } = useUrlStore();
  const { defaultServiceTypes, shouldAutoApply } = useWorkspaceAutoFilters();

  // sorting state (always use server-side sorting now)
  const [sortColumns, setSortColumns] = useState<readonly ServerSortColumn[]>(
    [],
  );

  // fetch file data to get services list
  const { data: fileData } = useGetFileByIdQuery(
    { id: fileId.toString() },
    { skip: !fileId },
  );

  const [extractDoc, setExtractDoc] = useState<DocumentType | null>(null);

  const activeService = useMemo(() => {
    const file = fileData ?? { id: fileId, claim_numbers: [] };
    if (serviceId && serviceType) {
      return {
        file,
        id: serviceId,
        type: serviceType,
        msa: serviceType === "msa" ? serviceId : undefined,
        costprojection:
          serviceType === "cost_projection" ? serviceId : undefined,
        document_management_file:
          serviceType === "document_management_file" ? serviceId : undefined,
      };
    }
    if (msaId) {
      const id = parseInt(msaId, 10);
      return { file, id, type: "msa", msa: id };
    }
    if (lcpId || mcpId) {
      const id = parseInt((lcpId || mcpId)!, 10);
      return { file, id, type: "cost_projection", costprojection: id };
    }
    if (documentManagementFileId) {
      const id = parseInt(documentManagementFileId, 10);
      return {
        file,
        id,
        type: "document_management_file",
        document_management_file: id,
      };
    }
    const firstSvc = fileData?.services?.[0];
    if (firstSvc) {
      return {
        file,
        id: firstSvc.id,
        type: firstSvc.type,
        msa: firstSvc.msa ?? undefined,
        costprojection: firstSvc.costprojection ?? undefined,
        document_management_file:
          firstSvc.document_management_file ?? undefined,
      };
    }
    return { file };
  }, [
    fileData,
    fileId,
    serviceId,
    serviceType,
    msaId,
    lcpId,
    mcpId,
    documentManagementFileId,
  ]);

  // apply workspace-based filters on mount
  useEffect(() => {
    if (shouldAutoApply && defaultServiceTypes.length > 0) {
      setFilter("service_type", defaultServiceTypes);
    }
  }, []);

  const {
    documents,
    totalCount,
    page,
    setPage,
    isLoading,
    isUploading,
    handleUpload,
    handleDelete,
    documentSearchTerm,
  } = useDocumentTable({
    fileId,
    serviceId,
    serviceType,
    additionalParams,
    sortBy: convertSortToOrderBy(sortColumns),
  });

  const [bulkDownloadDocuments, { isLoading: isBulkDownloading }] =
    useBulkDownloadDocumentsMutation();
  // Separate instance so Download All keeps its own loading state.
  const [bulkDownloadSelected, { isLoading: isDownloadingSelected }] =
    useBulkDownloadDocumentsMutation();
  const [bulkDeleteDocuments, { isLoading: isBulkDeleting }] =
    useBulkDeleteDocumentsMutation();
  const isBulkSelectMode =
    !metaDoc &&
    Boolean(
      constants?.FEATURE_FLAGS?.[FEATURE_FLAGS.DOCUMENTS_MULTI_SELECT_ENABLED],
    );
  const [bulkSelectedIds, setBulkSelectedIds] = useState<Set<number>>(
    new Set(),
  );
  const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false);
  const hasBulkSelection = bulkSelectedIds.size > 0;
  const bulkSelectionCountLabel = hasBulkSelection
    ? ` (${bulkSelectedIds.size})`
    : "";
  const isBulkActionPending =
    isLoading || isBulkDownloading || isDownloadingSelected || isBulkDeleting;

  // Search and filters change which documents match; paging and sorting don't.
  useEffect(() => {
    setBulkSelectedIds(new Set());
  }, [location.search, fileId]);
  const { data: activeMetadoc } = useGetMetadocQuery(parsedMetadocId ?? 0, {
    skip: !metaDoc || !parsedMetadocId,
  });
  const [addDocumentsToMetadoc, { isLoading: isAddingDocumentsToMetadoc }] =
    useAddDocumentsToMetadocMutation();
  const isMetadocCloningEnabled =
    constants?.FEATURE_FLAGS?.[FEATURE_FLAGS.METADOC_CLONING_ENABLED];
  const isAddDocumentsMode =
    Boolean(parsedMetadocId) &&
    isMetadocCloningEnabled &&
    activeMetadoc?.clone_reason === "ai_analysis";
  const metadocNavigationContext = useMemo(
    () =>
      getMetadocNavigationContext(location.pathname, {
        fileId: routeFileId,
        documentManagementFileId,
        msaId,
        mcpId,
        lcpId,
        reviewId,
      }),
    [
      location.pathname,
      routeFileId,
      documentManagementFileId,
      msaId,
      mcpId,
      lcpId,
      reviewId,
    ],
  );

  const { createMetadoc } = useCreateMetadoc({
    fileId: parseInt(fileId.toString(), 10),
    navigationContext: metadocNavigationContext ?? undefined,
    onSuccess: () => {
      // clear selection after successful creation
      if (onSelectionChange) {
        onSelectionChange(new Set());
      }
    },
  });

  const [deleteModalState, setDeleteModalState] = useState<DeleteModalState>({
    isOpen: false,
    document: null,
  });
  const [isDeleting, setIsDeleting] = useState(false);

  const onDrop = (acceptedFiles: File[]) => {
    if (acceptedFiles && acceptedFiles.length > 0) {
      setPendingFiles(acceptedFiles);
    }
  };

  const handleConfirmUpload = async (results: UploadMetadataResult) => {
    if (results.length === 0) return;
    setPendingFiles(null);
    setSelectedFile(results.map((result) => result.file));

    // Upload independently in parallel, suppress each toast, and show
    // one summary once everything settles
    const outcomes = await Promise.allSettled(
      results.map((result) =>
        handleUpload(
          [result.file],
          result.documentType,
          result.arScreeningDocumentType,
          undefined,
          result.serviceFields,
          true,
        ),
      ),
    );
    setSelectedFile(null);

    const successCount = outcomes.filter(
      (outcome) => outcome.status === "fulfilled" && outcome.value,
    ).length;
    const failureCount = outcomes.length - successCount;

    if (failureCount === 0) {
      KaboodleToaster.success(
        outcomes.length > 1
          ? `${outcomes.length} documents uploaded successfully`
          : "Document uploaded successfully",
      );
    } else if (successCount > 0) {
      KaboodleToaster.error(
        `Uploaded ${successCount} of ${outcomes.length} files. ${failureCount} failed.`,
      );
    } else {
      // Surface the first real failure instead of a generic message. Both
      // the presigned and legacy upload paths throw (not return false) on
      // failure when suppressToast is true, so this is always a real
      // rejection here, never a silently-failed fulfilled result.
      const firstRejection = outcomes.find(
        (outcome): outcome is PromiseRejectedResult =>
          outcome.status === "rejected",
      );
      const message =
        firstRejection?.reason instanceof Error
          ? firstRejection.reason.message
          : undefined;
      KaboodleToaster.error(message || "Failed to upload document(s)");
    }
  };

  const handleCancelUpload = () => {
    setPendingFiles(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const { data: organizationSettings } = useGetOrganizationSettingsQuery();

  const { getRootProps, getInputProps, isDragActive } = useDocumentDropzone({
    onDrop,
    disabled:
      isUploading ||
      isLoading ||
      isAddingDocumentsToMetadoc ||
      pendingFiles !== null,
    noClick: true,
    noKeyboard: true,
    allowMp4: !!organizationSettings?.allow_mp4_uploads,
  });

  const handleChooseFiles = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleDocumentDelete = (doc: DocumentType) => {
    setDeleteModalState({ isOpen: true, document: doc });
  };

  const handleConfirmDelete = async () => {
    if (!deleteModalState.document || isDeleting) return;

    setIsDeleting(true);
    try {
      const success = await handleDelete(deleteModalState.document);
      if (success) {
        setDeleteModalState({ isOpen: false, document: null });
        if (bulkSelectedIds.has(deleteModalState.document.id)) {
          const updatedBulkSelection = new Set(bulkSelectedIds);
          updatedBulkSelection.delete(deleteModalState.document.id);
          setBulkSelectedIds(updatedBulkSelection);
        }
        // remove deleted document from selection if it was selected
        if (metaDoc && selectedDocumentIds.has(deleteModalState.document.id)) {
          const updatedSelection = new Set(selectedDocumentIds);
          updatedSelection.delete(deleteModalState.document.id);
          onSelectionChange?.(updatedSelection);
        }
      }
    } catch (error) {
      console.error("Failed to delete:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleBulkDownload = async () => {
    if (!fileId || documents.length === 0 || isBulkDownloading) return;

    try {
      await bulkDownloadDocuments({
        fileId,
      }).unwrap();

      KaboodleToaster.success(
        "Bulk download request submitted. You will receive an email when your download is ready.",
      );
    } catch (error: unknown) {
      KaboodleToaster.error(
        getApiErrorMessage(error, "Failed to submit bulk download request"),
      );
    }
  };

  const handleBulkDownloadSelected = async () => {
    if (!fileId || bulkSelectedIds.size === 0 || isDownloadingSelected) return;

    try {
      await bulkDownloadSelected({
        fileId,
        documentIds: Array.from(bulkSelectedIds),
      }).unwrap();

      setBulkSelectedIds(new Set());
      KaboodleToaster.success(
        "Bulk download request submitted. You will receive an email when your download is ready.",
      );
    } catch (error: unknown) {
      KaboodleToaster.error(
        getApiErrorMessage(error, "Failed to submit bulk download request"),
      );
    }
  };

  const handleConfirmBulkDelete = async () => {
    if (bulkSelectedIds.size === 0 || isBulkDeleting) return;

    try {
      const { deleted_count } = await bulkDeleteDocuments({
        documentIds: Array.from(bulkSelectedIds),
      }).unwrap();

      setIsBulkDeleteModalOpen(false);
      setBulkSelectedIds(new Set());
      KaboodleToaster.success(
        deleted_count === 1
          ? "Document deleted successfully"
          : `${deleted_count} documents deleted successfully`,
      );
    } catch (error: unknown) {
      KaboodleToaster.error(
        getApiErrorMessage(error, "Failed to delete documents"),
      );
    }
  };

  const handleProcessDocuments = async () => {
    if (
      !metaDoc ||
      selectedDocumentIds.size === 0 ||
      isAddingDocumentsToMetadoc
    )
      return;

    if (isAddDocumentsMode) {
      if (!activeMetadoc?.id || !activeMetadoc.version_hash) {
        KaboodleToaster.error(
          "MetaDoc is missing version info. Please refresh and try again.",
        );
        return;
      }

      try {
        await addDocumentsToMetadoc({
          metadocId: activeMetadoc.id,
          documentIds: Array.from(selectedDocumentIds),
          versionHash: activeMetadoc.version_hash,
        }).unwrap();

        dispatch(setShowDocumentsDrawer(false));
        onSelectionChange?.(new Set());
        KaboodleToaster.success("Document addition started");
      } catch (error: unknown) {
        const errorMessage =
          error && typeof error === "object" && "data" in error
            ? ((error.data as { error?: string })?.error ??
              "Failed to add documents to MetaDoc")
            : "Failed to add documents to MetaDoc";

        KaboodleToaster.error(errorMessage);
      }

      return;
    }

    await createMetadoc(selectedDocumentIds);
  };

  const isMetaDocEnabled =
    constants?.FEATURE_FLAGS?.[FEATURE_FLAGS.METADOC_ENABLED];

  const paginationLeftContent = useMemo(() => {
    if (!metaDoc || !isMetaDocEnabled) return undefined;

    return (
      <>
        <Button
          onClick={handleProcessDocuments}
          disabled={
            selectedDocumentIds.size === 0 || isAddingDocumentsToMetadoc
          }
          className="allan-koba"
        >
          {isAddingDocumentsToMetadoc
            ? "Adding Documents..."
            : `${isAddDocumentsMode ? "Add Documents to MetaDoc" : "Process Selected Documents"}${
                selectedDocumentIds.size > 0
                  ? ` (${selectedDocumentIds.size})`
                  : ""
              }`}
        </Button>
        {selectedDocumentIds.size > 0 && (
          <Button
            onClick={() => onSelectionChange?.(new Set())}
            className="clear-selection-btn"
            style={{ marginLeft: "8px" }}
            disabled={isAddingDocumentsToMetadoc}
          >
            Clear Selection
          </Button>
        )}
      </>
    );
  }, [
    metaDoc,
    isMetaDocEnabled,
    isAddDocumentsMode,
    isAddingDocumentsToMetadoc,
    selectedDocumentIds.size,
    handleProcessDocuments,
    onSelectionChange,
  ]);

  const renderContent = useMemo(() => {
    // show upload progress
    if (isUploading) {
      return (
        <div className="documents--Loading">
          <LoadingIcon
            message={`Uploading ${selectedFile?.length ?? 0} Attachments`}
          />
          <div className="loading-sub-text">This may take a moment</div>
        </div>
      );
    }

    if (isAddingDocumentsToMetadoc) {
      return (
        <div className="documents--Loading">
          <LoadingIcon message="Adding Documents to MetaDoc" />
          <div className="loading-sub-text">This may take a moment</div>
        </div>
      );
    }

    // show loading state
    if (isLoading) {
      return (
        <div className="documents--Loading">
          <LoadingIcon message="Loading Documents" />
        </div>
      );
    }

    // show empty state
    if (documents.length === 0) {
      return (
        <div className="empty-state">
          <div className="empty-state-content">
            <span className="material-icons">folder</span>
            <h3>
              {documentSearchTerm
                ? "No Documents Found"
                : "No Documents Available"}
            </h3>
            <p>Drag and drop files here or click the Upload button</p>
          </div>
        </div>
      );
    }

    // render table with server-side pagination/sorting
    return (
      <DocumentTable
        documents={documents}
        totalCount={totalCount}
        page={page}
        onPageChange={setPage}
        sortColumns={sortColumns}
        onSortChange={setSortColumns}
        onDelete={handleDocumentDelete}
        onExtractPayHistory={(doc) => setExtractDoc(doc)}
        isLoading={isLoading}
        isDrawer={showDocumentsDrawer}
        fileServices={fileData?.services || []}
        fileId={fileId}
        metaDoc={metaDoc}
        selectable={isBulkSelectMode}
        selectedDocumentIds={
          isBulkSelectMode ? bulkSelectedIds : selectedDocumentIds
        }
        onSelectionChange={
          isBulkSelectMode ? setBulkSelectedIds : onSelectionChange
        }
        paginationLeftContent={paginationLeftContent}
      />
    );
  }, [
    isUploading,
    isAddingDocumentsToMetadoc,
    selectedFile,
    documentSearchTerm,
    documents,
    totalCount,
    page,
    setPage,
    sortColumns,
    handleDocumentDelete,
    isLoading,
    showDocumentsDrawer,
    fileData,
    fileId,
    metaDoc,
    isBulkSelectMode,
    bulkSelectedIds,
    selectedDocumentIds,
    onSelectionChange,
    paginationLeftContent,
  ]);

  return (
    <>
      <div
        className={cx("documents documents-modal flex jc-se", {
          open: showDocumentsDrawer,
          "documents-modal--drag-active": isDragActive,
        })}
      >
        {/* parent dropzone to detect drag of documents and show overlay */}
        <div
          {...getRootProps({
            preventDropOnDocument: true,
            className: "documents-wrapper w-search",
          })}
        >
          <input {...getInputProps()} ref={fileInputRef} />
          <div
            className="header-primary flex jc-sb ai-c"
            style={{ flexShrink: 0 }}
          >
            <div
              className="flex ai-c"
              style={{ gap: "16px", zIndex: 10, position: "relative" }}
              onMouseDown={(e) => e.stopPropagation()} // Prevent drag start from interfering with header clicks
            >
              Documents
            </div>
            <div className="header-actions" style={{ display: "flex" }}>
              <Button
                className="upload-btn-small"
                onClick={handleChooseFiles}
                disabled={
                  isUploading ||
                  isLoading ||
                  isAddingDocumentsToMetadoc ||
                  pendingFiles !== null
                }
              >
                <span className="material-icons">upload</span>
                Upload
              </Button>
              {documents.length > 0 && !metaDoc && (
                <Button
                  className="upload-btn-small download-all-btn"
                  onClick={handleBulkDownload}
                  disabled={isBulkActionPending}
                >
                  <span className="material-icons">
                    {isBulkDownloading ? "hourglass_empty" : "download"}
                  </span>
                  {isBulkDownloading ? "Requesting..." : "Download All"}
                </Button>
              )}
              {documents.length > 0 && isBulkSelectMode && (
                <>
                  <Button
                    className="upload-btn-small download-all-btn"
                    onClick={handleBulkDownloadSelected}
                    disabled={!hasBulkSelection || isBulkActionPending}
                  >
                    <span className="material-icons">
                      {isDownloadingSelected ? "hourglass_empty" : "download"}
                    </span>
                    {isDownloadingSelected
                      ? "Requesting..."
                      : `Download${bulkSelectionCountLabel}`}
                  </Button>
                  <Button
                    className="upload-btn-small download-all-btn delete-selected-btn"
                    onClick={() => setIsBulkDeleteModalOpen(true)}
                    disabled={!hasBulkSelection || isBulkActionPending}
                  >
                    <span className="material-icons">delete</span>
                    {`Delete${bulkSelectionCountLabel}`}
                  </Button>
                  <Button
                    className="upload-btn-small download-all-btn"
                    onClick={() => setBulkSelectedIds(new Set())}
                    disabled={!hasBulkSelection || isBulkActionPending}
                  >
                    <span className="material-icons">close</span>
                    Clear
                  </Button>
                </>
              )}
              <a
                className="header-primary alt-btn"
                onClick={() => {
                  if (!isAddingDocumentsToMetadoc) {
                    dispatch(setShowDocumentsDrawer(false));
                  }
                }}
              >
                Close
              </a>
            </div>
          </div>

          <div className="search-container" style={{ flexShrink: 0 }}>
            <SearchBar
              override={true}
              variant="documents"
              filterType="documents"
            />
          </div>

          <div className="data-values mrg-top">{renderContent}</div>

          {/* only rendered during drag */}
          {isDragActive && (
            <div className="documents-drag-overlay">
              <div className="documents-drag-overlay__content">
                <div className="documents-drag-overlay__icon">
                  <span className="material-icons">cloud_upload</span>
                </div>
                <h3>Drop files here to upload</h3>
                <p>Release to upload your documents</p>
              </div>
            </div>
          )}
        </div>
      </div>

      <DeleteConfirmationModal
        isOpen={deleteModalState.isOpen}
        onClose={() => {
          if (!isDeleting) {
            setDeleteModalState({ isOpen: false, document: null });
          }
        }}
        onConfirm={handleConfirmDelete}
        fileName={deleteModalState.document?.original_filename || ""}
        isDeleting={isDeleting}
      />

      <DeleteConfirmationModal
        isOpen={isBulkDeleteModalOpen}
        onClose={() => {
          if (!isBulkDeleting) {
            setIsBulkDeleteModalOpen(false);
          }
        }}
        onConfirm={handleConfirmBulkDelete}
        count={bulkSelectedIds.size}
        isDeleting={isBulkDeleting}
      />

      <UploadMetadataGate
        isOpen={pendingFiles !== null && pendingFiles.length > 0}
        files={pendingFiles || []}
        serviceType={serviceType}
        fileServices={fileData?.services || []}
        onCancel={handleCancelUpload}
        onConfirm={handleConfirmUpload}
      />

      {extractDoc && (
        <ExtractPayHistoryModal
          isOpen={Boolean(extractDoc)}
          onClose={() => setExtractDoc(null)}
          service={activeService}
          preselectedDocumentId={extractDoc.id}
        />
      )}
    </>
  );
};

export default Documents;
